import {
  IconButton,
  Mark,
  Slider,
  Theme,
  Tooltip,
  Typography,
  useTheme,
  ValueLabelProps,
} from "@material-ui/core";
import { addSeconds, differenceInSeconds, format, parseISO } from "date-fns";
import { enUS } from "date-fns/locale";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Button, LinearProgress, useLocale } from "react-admin";
import { handleAxiosError } from "../../common/data/axiosErrorHandler";
import {
  LocationApiFactory,
  LocationHistoryEntryDto,
} from "../../common/data/service-proxies";
import { useAxiosInstance } from "../../common/hooks/useAxiosInstance";
import {
  dateFnsLocaleMap,
  ProLogsLocale,
} from "../../common/util/localization";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import { useHistory } from "react-router-dom";
import resourceNames from "../../common/data/resourceNames";
import { makeStyles } from "@material-ui/styles";
import LinkIcon from "@material-ui/icons/Link";
import { useActionPermissions } from "../../common/auth/useActionPermissions";
import permissionNames from "../../common/auth/access-control/permissionNames";
import { AssignmentLate, Build, Business, Home } from "@material-ui/icons";
import { GoogleMapControls } from "../locations/types";
import { AppState } from "../../common/types";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";

interface Props {
  vehicleId?: number | null;
  name?: string | null;
  isTruck: boolean;
  date: Date;
  onSetHighlightedRoute: (
    highlightedRoute?: Array<{ lat: number; lng: number }>
  ) => void;
  onSetVisitedRoute: (
    visitedRoute?: Array<{ lat: number; lng: number }>
  ) => void;
  onSetPosition: (position?: number) => void;
  onShareLinksClick: () => void;
  onPlayingChange: (isPlaying: boolean) => void;
  mapRef: RefObject<GoogleMapControls>;
  driverId?: number | null;
  onSetCurrentLocation?: (location?: LocationHistoryEntryDto) => void;
}

const useStyles = makeStyles<Theme, { loading: boolean }>((theme) => ({
  root: ({ loading }) => ({
    visibility: loading ? "visible" : "hidden",
    width: "100%",
    position: "absolute",
  }),
}));

const getLocationTime = (id: number, locale: Locale) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setSeconds(id);
  return format(date, "p", { locale });
};

const ValueLabelComponent = (props: ValueLabelProps) => {
  let { children, open, value } = props;
  value = value ?? "00:00";
  return (
    <Tooltip open={open} enterTouchDelay={0} placement="top" title={value}>
      {children}
    </Tooltip>
  );
};

export const DashboardVehicleControls = ({
  vehicleId,
  driverId,
  name,
  isTruck,
  date,
  onSetHighlightedRoute,
  onSetVisitedRoute,
  onSetPosition,
  onShareLinksClick,
  onPlayingChange,
  onSetCurrentLocation,
  mapRef,
}: Props) => {
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(true);
  const [dayStart, setDayStart] = useState<Date>();
  const [data, setData] = useState<Array<LocationHistoryEntryDto>>([]);
  const [timeZone, setTimeZone] = useState<string>();
  const [second, setSecond] = useState<number>();
  const [marks, setMarks] = useState<Mark[]>();
  const [currentLocation, setCurrentLocation] = useState<
    LocationHistoryEntryDto
  >();

  const [playing, setPlaying] = useState<boolean>(false);

  const axiosInstance = useAxiosInstance();
  const locale = useLocale() as ProLogsLocale;
  const dateFnsLocale = !!dateFnsLocaleMap[locale]
    ? dateFnsLocaleMap[locale]
    : enUS;

  const classes = useStyles({ loading });
  const secondRef = useRef<number>();
  const actionPermissions = useActionPermissions();

  const theme: Theme = useTheme();

  const themeName = useSelector((x: AppState) => x.theme);

  const handleSetCurrentLocation = useCallback(
    (loc?: LocationHistoryEntryDto) => {
      if (onSetCurrentLocation) {
        onSetCurrentLocation(loc);
      }

      setCurrentLocation(loc);
    },
    [onSetCurrentLocation]
  );

  useEffect(() => {
    secondRef.current = second;
  }, [second]);

  useEffect(() => {
    if (!playing) {
      onPlayingChange(false);
      return;
    } else {
      onPlayingChange(true);
    }

    if (!dayStart) {
      return;
    }

    const interval = setInterval(() => {
      secondRef.current = secondRef.current ?? 0;
      const sec =
        secondRef.current <=
          differenceInSeconds(
            parseISO(data[data.length - 1].timeStamp!),
            dayStart
          )
          ? secondRef.current + 60
          : differenceInSeconds(parseISO(data[0].timeStamp!), dayStart);
      const dataItem = data.find(
        (x) => parseISO(x.timeStamp!) >= addSeconds(dayStart, sec)
      );
      if (dataItem) {
        handleSetCurrentLocation(dataItem);
      }

      onSetPosition(sec);
      setSecond(sec);
    }, 100);

    return () => clearInterval(interval);
  }, [
    playing,
    dayStart,
    data,
    onSetPosition,
    isTruck,
    mapRef,
    vehicleId,
    onPlayingChange,
    handleSetCurrentLocation,
  ]);

  useEffect(() => {
    if (!axiosInstance) {
      return;
    }

    if (!vehicleId && isTruck) {
      return;
    }

    if (!name && !isTruck) {
      return;
    }

    const factory = LocationApiFactory(
      undefined,
      axiosInstance.defaults.baseURL,
      axiosInstance
    );
    const locationHistoryMethod = isTruck
      ? () =>
        factory.apiAppLocationVehicleLocationHistoryGet(
          vehicleId!,
          date.toISOString()
        )
      : () =>
        factory.apiAppLocationTrailerLocationHistoryGet(
          name!,
          date.toISOString()
        );

    setLoading(true);
    locationHistoryMethod()
      .then((result) => {
        if (!result.data.locations || !result.data.locations.length) {
          setData([]);
          handleSetCurrentLocation(undefined);
          setMarks(undefined);
          setDayStart(undefined);
          setTimeZone(result.data.homeTerminalTimeZone ?? undefined);
          return;
        }

        setData(result.data.locations ?? []);
        setTimeZone(result.data.homeTerminalTimeZone ?? undefined);
        handleSetCurrentLocation(
          result.data.locations[result.data.locations.length - 1]
        );
        setSecond(
          differenceInSeconds(
            parseISO(
              result.data.locations[result.data.locations.length - 1].timeStamp!
            ),
            parseISO(result.data.startOfDay!)
          )
        );
        mapRef.current?.panToCoordinates(
          result.data.locations.map((x) => ({ lat: x.lat, lng: x.lng }))
        );
        const timestamps = result.data.locations.map((x) => {
          const seconds = differenceInSeconds(
            parseISO(x.timeStamp!),
            parseISO(result.data.startOfDay!)
          );
          if (!seconds) {
            console.warn(
              `Unable to get seconds for timestamp '${x.timeStamp}'`
            );
          }

          return seconds;
        });
        const uniqueTimestamps = new Set(timestamps);
        setMarks(
          Array.from(uniqueTimestamps).map((x) => ({ value: x, label: "" }))
        );
        setDayStart(parseISO(result.data.startOfDay!));
      })
      .catch((e) => {
        console.error(handleAxiosError(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    axiosInstance,
    date,
    vehicleId,
    name,
    isTruck,
    mapRef,
    handleSetCurrentLocation,
  ]);

  useEffect(() => {
    if (!data || !currentLocation || !dayStart) {
      onSetHighlightedRoute(undefined);
      onSetVisitedRoute(undefined);
      return;
    }

    const locationIndex = data.indexOf(currentLocation);
    const visited = [...data.slice(0, locationIndex), currentLocation];
    const highlighted = data.slice(locationIndex);

    onSetHighlightedRoute(
      highlighted.map((x) => ({ lat: x.lat!, lng: x.lng! }))
    );
    onSetVisitedRoute(visited.map((x) => ({ lat: x.lat!, lng: x.lng! })));
  }, [
    data,
    currentLocation,
    dayStart,
    onSetVisitedRoute,
    onSetHighlightedRoute,
  ]);

  useEffect(() => {
    if (!data || !currentLocation || !dayStart) {
      onSetHighlightedRoute(undefined);
      onSetVisitedRoute(undefined);
      return;
    }

    const locationIndex = data.indexOf(currentLocation);
    const visited = [...data.slice(0, locationIndex), currentLocation];
    const highlighted = data.slice(locationIndex);

    onSetHighlightedRoute(
      highlighted.map((x) => ({ lat: x.lat!, lng: x.lng! }))
    );
    onSetVisitedRoute(visited.map((x) => ({ lat: x.lat!, lng: x.lng! })));
  }, [
    data,
    currentLocation,
    dayStart,
    onSetHighlightedRoute,
    onSetVisitedRoute,
  ]);

  return (
    <div className="control history-control">
      {createPortal(
        <div
          style={{
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.background.paper,
            padding: 8,
            marginRight: 8,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", flexDirection: "row", gap: 6 }}>
              <Business fontSize="inherit" />
              <p style={{ margin: 0, fontSize: 10 }}>{timeZone ?? "-"}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: 6 }}>
              <Home fontSize="inherit" />
              <p style={{ margin: 0, fontSize: 10 }}>
                {Intl.DateTimeFormat().resolvedOptions().timeZone ??
                  "Can't resolve timezone"}
              </p>
            </div>{" "}
          </div>
        </div>,
        document.getElementById("dashboard-map-settings-timezone-info")!
      )}
      <div className="control-label">
        <Typography variant="body2" color="textSecondary">
          Location history
        </Typography>
      </div>
      <div className="slider-container">
        <LinearProgress classes={{ ...classes }} color="primary" />
        {!loading && data.length && dayStart ? (
          <>
            <div>
              <IconButton onClick={() => setPlaying(!playing)}>
                {playing ? <StopIcon /> : <PlayArrowIcon />}
              </IconButton>
            </div>
            <Slider
              min={0}
              max={86399}
              step={60}
              value={second ?? 0}
              onChange={(_, v) => {
                const sec = v as number;
                const dataItem = data.find(
                  (x) => parseISO(x.timeStamp!) >= addSeconds(dayStart, sec)
                );
                if (dataItem) {
                  handleSetCurrentLocation(dataItem);
                }

                onSetPosition(sec);
                setSecond(sec);
              }}
              ValueLabelComponent={ValueLabelComponent}
              valueLabelDisplay="on"
              valueLabelFormat={(id) => getLocationTime(id, dateFnsLocale)}
              marks={marks}
              track={false}
            />
          </>
        ) : !loading && !data.length ? (
          <Typography variant="body2" color="textSecondary">
            No vehicle location data to display
          </Typography>
        ) : null}
      </div>
      <div
        className={`keep-right buttons ${themeName === "dark" ? "keep-right-dark" : ""
          }`}
      >
        {isTruck &&
          actionPermissions &&
          actionPermissions[permissionNames.locations.current.liveTracking] ? (
          <Button
            label="Live tracking"
            onClick={() =>
              history.push(`/${resourceNames.LOCATIONS_LIVETRACK}/${vehicleId}`)
            }
          >
            <GpsFixedIcon />
          </Button>
        ) : null}
        {isTruck &&
          actionPermissions &&
          actionPermissions[
          permissionNames.locations.current.manageShareLinks
          ] ? (
          <Button label="Share links" onClick={onShareLinksClick}>
            <LinkIcon />
          </Button>
        ) : null}
        {isTruck &&
          actionPermissions &&
          actionPermissions[permissionNames.assets.diagnostics.main] ? (
          <Button
            label="Maintenance"
            onClick={() =>
              history.push(
                `/${resourceNames.ASSETS_MAINTENANCE_DASHBOARD}/${vehicleId}`
              )
            }
          >
            <Build />
          </Button>
        ) : null}

        {isTruck &&
          actionPermissions &&
          actionPermissions[permissionNames.safety.alerts.main] &&
          driverId ? (
          <Button
            label="Safety alerts"
            onClick={() =>
              history.push(`/${resourceNames.SAFETY_DASHBOARD}/${driverId}`)
            }
          >
            <AssignmentLate />
          </Button>
        ) : null}
      </div>
    </div>
  );
};
